import { Extension, Mark, mergeAttributes } from '@tiptap/core';

/**
 * Shared `changeId` attribute for the diff marks.
 * @returns {object} TipTap attribute spec.
 */
const changeIdAttribute = () => ({
  changeId: {
    default: null,
    parseHTML: (element) => element.getAttribute('data-change-id'),
    renderHTML: (attributes) => (attributes.changeId ? { 'data-change-id': attributes.changeId } : {}),
  },
});

/**
 * DiffDelete mark - highlights text that is proposed for deletion during AI review.
 * Carries a `changeId` attribute to group related changes. Styled by the editor
 * (`.q-diff-delete`), so it follows the theme.
 *
 * @example
 * editor.commands.setMark('diffDelete', { changeId: '1' })
 */
const DiffDelete = Mark.create({
  name: 'diffDelete',
  excludes: '',
  inclusive: false,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return changeIdAttribute();
  },

  parseHTML() {
    return [{ tag: 'span[data-diff-delete]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-diff-delete': 'true',
        class: 'q-diff-delete',
      }),
      0,
    ];
  },
});

/**
 * DiffInsert mark - highlights text that is proposed for insertion during AI review.
 *
 * @example
 * editor.commands.setMark('diffInsert', { changeId: '1' })
 */
const DiffInsert = Mark.create({
  name: 'diffInsert',
  excludes: '',
  inclusive: false,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return changeIdAttribute();
  },

  parseHTML() {
    return [{ tag: 'span[data-diff-insert]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-diff-insert': 'true',
        class: 'q-diff-insert',
      }),
      0,
    ];
  },
});

/** Block and atom node types that can be marked as wholly inserted or deleted during review. */
export const DIFF_BLOCK_TYPES = [
  'paragraph',
  'heading',
  'blockquote',
  'bulletList',
  'orderedList',
  'listItem',
  'taskList',
  'taskItem',
  'codeBlock',
  'horizontalRule',
  'table',
  'tableRow',
  'image',
  'mermaidBlock',
  'trustSection',
  'templateFieldBlock',
  'generatedField',
  // An atom with no inline content: without the attribute a page break Inky inserts or deletes
  // could not be tracked (or rejected) as a review change.
  'pageBreak',
];

/**
 * Global `data-diff-block="insert|delete"` + `data-change-id` attributes on blocks and atoms.
 * Marks cannot be applied to images, Mermaid diagrams or whole blocks, so review changes
 * that add or remove complete blocks are tracked on the node itself. Accepting/rejecting a
 * change deletes the node or clears the attribute; nothing of this ever reaches saved HTML.
 */
const DiffBlockAttributes = Extension.create({
  name: 'diffBlockAttributes',

  addGlobalAttributes() {
    return [
      {
        types: DIFF_BLOCK_TYPES,
        attributes: {
          diffBlock: {
            default: null,
            keepOnSplit: false,
            parseHTML: (element) => {
              const value = element.getAttribute('data-diff-block');
              return value === 'insert' || value === 'delete' ? value : null;
            },
            renderHTML: (attributes) => (attributes.diffBlock ? { 'data-diff-block': attributes.diffBlock } : {}),
          },
          diffChangeId: {
            default: null,
            keepOnSplit: false,
            parseHTML: (element) => (element.hasAttribute('data-diff-block') ? element.getAttribute('data-change-id') : null),
            renderHTML: (attributes) => (
              attributes.diffBlock && attributes.diffChangeId ? { 'data-change-id': attributes.diffChangeId } : {}
            ),
          },
        },
      },
    ];
  },
});

export { DiffDelete, DiffInsert, DiffBlockAttributes };
